import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { toError } from "@clipboard-health/util-ts";

import { APP_V2_APP_EVENTS, logError } from "../analytics";

/**
 * Strips query parameters and fragments from a URL to avoid logging sensitive data.
 */
function redactUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    // If URL parsing fails, return a safe placeholder
    return "[invalid-url]";
  }
}

/**
 * Opens a URL in the device's browser.
 * On native platforms, opens in an in-app browser.
 * On web, opens in a new tab.
 *
 * @param url - The URL to open
 * @param source - Optional source identifier for logging purposes
 */
export async function openInBrowser(url: string, source?: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      await Browser.open({
        url,
        presentationStyle: "fullscreen",
      });
    } catch (maybeError: unknown) {
      logError(APP_V2_APP_EVENTS.OPEN_IN_BROWSER_FAILED, {
        error: toError(maybeError),
        metadata: {
          url: redactUrl(url),
          source,
          platform: "native",
        },
      });
      throw maybeError;
    }
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}
